import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import SearchablePicker from "@/components/SearchablePicker";
import { ReconSection, ReconExports, Status, fmt } from "@/components/wo-recon/ReconSection";

const DEPTS = [["ALL", "All Departments"], ["WARPING", "Warping"], ["COVERING", "Covering"], ["SPANDEX_WARPING", "Spandex Warping"], ["WEAVING", "Weaving"]];
const n = (k, l, drill) => ({ k, l, num: true, drill });
const S = (k = "status", l = "Status") => ({ k, l, status: true });

const COLS = {
  a: [{ k: "department", l: "Department" }, { k: "cwo_no", l: "CWO" }, { k: "output_item", l: "Output Item" }, n("required", "Required"),
    n("produced", "Posted Produced", true), n("outstanding", "Outstanding"), { k: "uom", l: "UOM" }, { k: "cwo_status", l: "Lifecycle" },
    { k: "recon_status", l: "Reconciliation", status: true }, n("dept_produced", "Dept Recorded")],
  b: [{ k: "department", l: "Department" }, { k: "rm_code", l: "RM Item" }, { k: "uom", l: "UOM" }, { k: "reference", l: "CWO / WO" },
    n("required", "Required"), n("issued", "Issued"), n("consumption", "Consumption (usage + wastage)", true), n("wastage", "of which Wastage", true),
    n("dept_consumption", "Dept Consumption"), n("balance", "Balance"), S()],
  c: [{ k: "department", l: "Department" }, { k: "cwo_no", l: "CWO" }, { k: "output_type", l: "Type" }, { k: "item", l: "Item" },
    n("produced", "Produced", true), n("transfer_in", "Transfer In", true), n("transferred", "Transferred", true), n("consumed_next", "Consumed by Next", true),
    n("wastage", "Wastage", true), n("remaining", "Remaining (batch)"), { k: "uom", l: "UOM" }, S()],
  d: [{ k: "item", l: "Item" }, { k: "batch_lot", l: "Batch" }, { k: "source_document", l: "Source Document" }, { k: "wo_no", l: "WO" },
    { k: "cwo_no", l: "CWO" }, { k: "from", l: "From" }, { k: "to", l: "To" }, n("released", "Released", true), n("received", "Received", true),
    n("difference", "Difference"), { k: "uom", l: "UOM" }, { k: "method", l: "Method" }, S()],
  e: [{ k: "material_type", l: "Type" }, { k: "material", l: "Material" }, { k: "batch_lot", l: "Batch" }, { k: "source_cwo", l: "Source CWO" },
    n("received", "Received"), n("consumed", "Consumed (usage + wastage)", true), n("wastage", "of which Wastage", true),
    n("dept_consumed", "Dept Consumed"), S("dept_status", "Dept Status")],
  f: [{ k: "department", l: "Department" }, { k: "item", l: "Item" }, { k: "control", l: "Control" }, n("expected", "Expected"),
    n("actual", "Actual"), n("variance", "Variance"), S(), { k: "reference", l: "Reference" }],
};

function Header({ h }) {
  const f = [["Parent WO", h.parent_wo_no], [h.order_type || "SO / ESO", h.order_no], ["Product", h.product_code], ["Plan", h.plan_no],
    ["WO Status", h.wo_status], ["WO Required (m)", fmt(h.required_m)], ["Actual Woven (m)", fmt(h.produced_m)],
    ["Outstanding (m)", fmt(h.outstanding_m)], ["SO/ESO Ordered (m)", fmt(h.ordered_qty)], ["As of", h.as_of?.slice(0, 19).replace("T", " ")]];
  return (
    <div className="tactical-card p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="wor-header">
      {f.map(([k, v]) => (
        <div key={k}><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k}</div>
          <div className="font-mono text-sm text-white break-all" data-testid={`wor-h-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v ?? "—"}</div></div>
      ))}
    </div>
  );
}

export default function ConsolidatedWoReconciliation() {
  const [wos, setWos] = useState([]);
  const [wo, setWo] = useState("");
  const [dept, setDept] = useState("ALL");
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/reports/wo-consolidated-reconciliation/wos").then(r => setWos(r.data))
      .catch(e => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Not authorised"));
  }, []);
  const [ran, setRan] = useState(null);
  const run = async () => {
    setBusy(true);
    const params = { wo, department: dept };
    try { const { data } = await api.get("/reports/wo-consolidated-reconciliation", { params }); setRep(data); setRan(params); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Report failed"); }
    finally { setBusy(false); }
  };
  const opts = wos.map(w => ({ value: w.id, code: w.wo_no, name: w.product_code, meta: `${w.so_eso_no || "Not Linked"} · ${w.status}` }));
  const E = rep?.section_e?.[0];
  return (
    <div className="p-6 space-y-5" data-testid="wo-consolidated-recon-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Reports · Reconciliation</div>
        <h1 className="font-display text-4xl sm:text-5xl">Consolidated WO Reconciliation</h1>
        <p className="text-sm text-zinc-500 mt-1">Read-only · Parent WO-wise across Warping, Covering, Spandex Warping and Weaving · same data as the departmental reports.</p>
      </div>
      <div className="tactical-card p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[320px]"><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Parent WO *</div>
          <SearchablePicker options={opts} value={wo} onChange={setWo} placeholder="Select Parent WO" testid="wor-wo-picker" popoverClassName="w-[560px]" /></div>
        <div><div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Department</div>
          <select data-testid="wor-dept" value={dept} onChange={e => setDept(e.target.value)} className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm">
            {DEPTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <button type="button" data-testid="wor-run" onClick={run} disabled={!wo || busy}
          className="h-9 px-4 rounded-sm bg-[var(--accent)] text-white text-xs font-mono uppercase tracking-widest disabled:bg-zinc-700">{busy ? "Loading…" : "Run Report"}</button>
        <div className="ml-auto"><ReconExports params={ran || {}} disabled={!rep || !ran} /></div>
      </div>
      {rep && <>
        <Header h={rep.header} />
        <ReconSection id="a" title="A · Department-wise Production Summary" cols={COLS.a} rows={rep.section_a} drillTitle={r => r.cwo_no}
          note="Metres and kilograms are never summed · lifecycle and reconciliation status shown separately" />
        <ReconSection id="b" title="B · RM Material Accountability" cols={COLS.b} rows={rep.section_b} drillTitle={r => `${r.rm_code} · ${r.reference}`}
          note="Issue ≠ consumption · wastage is part of consumption, never deducted twice · Covering RM is a common pool" />
        <ReconSection id="c" title="C · SFG Production & Stock (SFW · SFC · SFSW)" cols={COLS.c} rows={rep.section_c} drillTitle={r => `${r.output_type} ${r.cwo_no}`} />
        <ReconSection id="d" title="D · Interdepartmental Material Flow" cols={COLS.d} rows={rep.section_d} drillTitle={r => r.source_document}
          note="Consume-in-place releases have no separate receiving transaction" />
        {E && <ReconSection id="e" title={`E · Weaving · Required ${fmt(E.required_m)} m · Woven ${fmt(E.produced_m)} m · Balance ${fmt(E.balance_m)} m`}
          cols={COLS.e} rows={E.materials} drillTitle={r => `${r.material_type} ${r.material}`} note={`Woven kg (Stock V2): ${fmt(E.produced_kg)} · secondary`} />}
        <ReconSection id="f" title="F · Reconciliation Control & Exceptions" cols={COLS.f} rows={rep.section_f} drillTitle={r => r.item} />
        <div className="text-[11px] text-zinc-500 flex gap-2 flex-wrap" data-testid="wor-legend">{["RECONCILED", "IN PROGRESS", "EXCEPTION", "INSUFFICIENT EVIDENCE"].map(s => <Status key={s} s={s} />)}</div>
      </>}
    </div>
  );
}
